import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {
    EiImportRecordStructureFieldConflictDto
} from '../../model/dto/entity/ei-import-record-structure-field-conflict.dto';
import {EiConflictResolutionStatus} from '../../enum/ei-conflict-resolution-status.enum';
import {EiConflictResolutionRequestDto} from '../../model/dto/api/ei-conflict-resolution-request.dto';
import {EiWorkQueueService} from '../../service/api/ei-work-queue.service';

@Component({
    selector: 'ei-conflict-resolution',
    templateUrl: './ei-conflict-resolution.component.html',
    styleUrls: ['./ei-conflict-resolution.component.scss'],
    standalone: false
})
export class EiConflictResolutionComponent implements OnInit, OnChanges {

    @Input() conflicts: EiImportRecordStructureFieldConflictDto[] = [];
    @Output() conflictResolved = new EventEmitter<EiImportRecordStructureFieldConflictDto>();
    @Output() allConflictsResolved = new EventEmitter<void>();

    selectedConflicts: EiImportRecordStructureFieldConflictDto[] = [];
    resolutionComment: string = '';
    showCommentDialog: boolean = false;
    pendingConflict: EiImportRecordStructureFieldConflictDto;
    pendingResolution: EiConflictResolutionStatus;

    // Resolution status enum for template
    ResolutionStatus = EiConflictResolutionStatus;

    constructor(private workQueueService: EiWorkQueueService) {
    }

    ngOnInit(): void {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['conflicts']) {
            this.selectedConflicts = [];
        }
    }

    get pendingConflictsCount(): number {
        return this.conflicts.filter(c => c.resolutionStatus === EiConflictResolutionStatus.PENDING).length;
    }

    acceptImport(conflict: EiImportRecordStructureFieldConflictDto): void {
        this.resolveConflict(conflict, EiConflictResolutionStatus.IMPORT_ACCEPTED);
    }

    keepSystem(conflict: EiImportRecordStructureFieldConflictDto): void {
        this.resolveConflict(conflict, EiConflictResolutionStatus.SYSTEM_KEPT);
    }

    openCommentDialog(conflict: EiImportRecordStructureFieldConflictDto, resolution: EiConflictResolutionStatus): void {
        this.pendingConflict = conflict;
        this.pendingResolution = resolution;
        this.resolutionComment = '';
        this.showCommentDialog = true;
    }

    confirmResolutionWithComment(): void {
        if (this.pendingConflict && this.pendingResolution !== undefined) {
            this.resolveConflict(this.pendingConflict, this.pendingResolution, this.resolutionComment);
            this.showCommentDialog = false;
            this.pendingConflict = null;
            this.pendingResolution = null;
            this.resolutionComment = '';
        }
    }

    cancelCommentDialog(): void {
        this.showCommentDialog = false;
        this.pendingConflict = null;
        this.pendingResolution = null;
        this.resolutionComment = '';
    }

    resolveConflict(conflict: EiImportRecordStructureFieldConflictDto, resolution: EiConflictResolutionStatus, comment?: string): void {
        const request: EiConflictResolutionRequestDto = {
            conflictId: conflict.id,
            resolution: resolution,
            comment: comment
        };

        this.workQueueService.resolveConflict(request).subscribe({
            next: () => {
                conflict.resolutionStatus = resolution;
                conflict.resolutionComment = comment;
                this.conflictResolved.emit(conflict);

                if (this.pendingConflictsCount === 0) {
                    this.allConflictsResolved.emit();
                }
            },
            error: (err) => {
                console.error('Error resolving conflict', err);
            }
        });
    }

    bulkAcceptImport(): void {
        this.bulkResolve(EiConflictResolutionStatus.IMPORT_ACCEPTED);
    }

    bulkKeepSystem(): void {
        this.bulkResolve(EiConflictResolutionStatus.SYSTEM_KEPT);
    }

    bulkResolve(resolution: EiConflictResolutionStatus): void {
        if (this.selectedConflicts.length === 0) {
            return;
        }

        const requests: EiConflictResolutionRequestDto[] = this.selectedConflicts.map(c => ({
            conflictId: c.id,
            resolution: resolution
        }));

        this.workQueueService.bulkResolve(requests).subscribe({
            next: (count) => {
                this.selectedConflicts.forEach(c => {
                    c.resolutionStatus = resolution;
                });
                this.selectedConflicts = [];

                if (this.pendingConflictsCount === 0) {
                    this.allConflictsResolved.emit();
                }
            },
            error: (err) => {
                console.error('Error bulk resolving conflicts', err);
            }
        });
    }

    getResolutionStatusLabel(status: EiConflictResolutionStatus): string {
        switch (status) {
            case EiConflictResolutionStatus.PENDING:
                return 'Offen';
            case EiConflictResolutionStatus.IMPORT_ACCEPTED:
                return 'Import akzeptiert';
            case EiConflictResolutionStatus.SYSTEM_KEPT:
                return 'System behalten';
            case EiConflictResolutionStatus.MERGED:
                return 'Zusammengeführt';
            case EiConflictResolutionStatus.AUTO_RESOLVED:
                return 'Automatisch';
            default:
                return 'Unbekannt';
        }
    }

    getResolutionStatusSeverity(status: EiConflictResolutionStatus) {
        switch (status) {
            case EiConflictResolutionStatus.PENDING:
                return 'warn';
            case EiConflictResolutionStatus.IMPORT_ACCEPTED:
                return 'info';
            case EiConflictResolutionStatus.SYSTEM_KEPT:
                return 'success';
            case EiConflictResolutionStatus.MERGED:
                return 'secondary';
            case EiConflictResolutionStatus.AUTO_RESOLVED:
                return 'contrast';
            default:
                return 'secondary';
        }
    }

}
